import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

import { Badge } from "~/components/ui";
import type { ChangeRequestReviewRow } from "~/features/changeRequests/types";

type BadgeTone = React.ComponentProps<typeof Badge>["tone"];

const REVIEW_TONE: Record<string, BadgeTone> = {
  approve: "status-approved",
  batchApprove: "status-approved",
  reject: "score-c",
  comment: "default",
};

interface ChangeRequestReviewHistoryProps {
  reviews: ChangeRequestReviewRow[];
}

export function ChangeRequestReviewHistory({ reviews }: ChangeRequestReviewHistoryProps) {
  const { t } = useTranslation();

  if (reviews.length === 0) {
    return <p className="py-2 text-[13px] text-fg-3">{t("feature.common.noData")}</p>;
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius-sm)] border border-border-default">
      <table className="w-full border-collapse text-[13px]">
        <thead className="bg-bg-subtle text-fg-3">
          <tr>
            <Th>{t("model.common.createdAt")}</Th>
            <Th>{t("model.user.modelName")}</Th>
            <Th>{t("feature.changeRequests.reviews.type")}</Th>
            <Th>{t("feature.changeRequests.reviews.comment")}</Th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((review) => (
            <tr key={review.id} className="border-t border-border-default align-top">
              <td className="whitespace-nowrap px-3 py-2 tabular-nums text-fg-2">
                {t("date.long", { date: parseISO(review.createdAt) })}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-fg-1">{review.userName}</td>
              <td className="px-3 py-2">
                <Badge tone={REVIEW_TONE[review.typeId] ?? "default"}>{review.typeId}</Badge>
              </td>
              <td className="whitespace-pre-line px-3 py-2 text-fg-1">{review.comment || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left text-12-medium font-medium">{children}</th>;
}
